import React, { useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaArrowLeft, FaEdit } from "react-icons/fa";
import adminApi from "services/admin-api";
import { formatDateTime, formatMoney, isRecoverableUploadResponseError } from "utils/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import RightDrawer from "components/global/RightDrawer";
import { AdminUserForm } from "components/admin/forms";
import { PageHeader, SectionCard, StatCard, StatusPill } from "components/admin/ui";
import { useAuth } from "context/auth-context";
import { formatPortalLabel, hasAnyPortalAction } from "utils/portal-access";
import { workspaceApi } from "pages/app/workspace/services";
import {
  ViewUserSecondaryButton,
  ViewUserPrimaryButton,
  ViewUserSectionSecondary,
  ViewUserGridLayoutQuaternary,
  ViewUserGridLayoutQuinary,
  ViewUserGridLayout,
  ViewUserGridLayoutSecondary,
  ViewUserGridLayoutTertiary,
  ViewUserDetailsGrid,
  ViewUserSection,
  ViewUserPreviewImage,
  ViewUserMetaText,
  ViewUserEyebrowText,
  ViewUserTitleText,
} from "pages/app/users/styles/view-user.page.inline.styles";
import {
  SchoolPlayerProfileBadgeRow,
  SchoolPlayerProfileEyebrowText,
  SchoolPlayerProfileHeroBody,
  SchoolPlayerProfileHeroEyebrow,
  SchoolPlayerProfileHeroImage,
  SchoolPlayerProfileHeroMeta,
  SchoolPlayerProfileHeroPanel,
  SchoolPlayerProfileHeroTextGroup,
  SchoolPlayerProfileHeroTitle,
  SchoolPlayerProfileMetadataGrid,
  SchoolPlayerProfileMetadataTile,
  SchoolPlayerProfileTitleText,
} from "pages/app/workspace/styles/school-player-profile.page.inline.styles";

export const ViewUserPage = () => {
  const history = useHistory();
  const location = useLocation<{
    backTo?: string;
    schoolId?: string;
  }>();
  const { id } = useParams<{
    id: string;
  }>();
  const queryClient = useQueryClient();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { activeSchoolId, portalAccess } = useAuth();
  const isSchoolRolesWorkspaceView = location.pathname.startsWith("/workspace/school-roles/");
  const scopedSchoolId = location.state?.schoolId || activeSchoolId || undefined;
  const canEditUser = hasAnyPortalAction(portalAccess, ["users.manage", "users.update"]);

  // TODO(api): Public/scoped staff user reads should not return wallet, orders, or payments.
  // We hide those sections in the school staff UI, but cannot change the API response here.
  const { data: user } = useQuery({
    queryKey: ["user", isSchoolRolesWorkspaceView ? scopedSchoolId || "global" : "global", id],
    queryFn: () =>
      isSchoolRolesWorkspaceView && scopedSchoolId
        ? adminApi.users.getScoped(scopedSchoolId, id)
        : adminApi.users.get(id),
    enabled: Boolean(id),
  });

  const { data: schoolAssignments = [] } = useQuery({
    queryKey: ["workspace-school-roles", scopedSchoolId],
    queryFn: () => workspaceApi.schoolRoles.list(scopedSchoolId),
    enabled: Boolean(isSchoolRolesWorkspaceView && scopedSchoolId),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: any) =>
      adminApi.users.update(id, {
        ...payload,
        password: payload.password || undefined,
        password_confirmation: payload.password_confirmation || undefined,
      }),
    onSuccess: () => {
      toast.success("User updated.");
      queryClient.invalidateQueries({
        queryKey: ["user"],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["showUserDetails"],
      });
      setDrawerOpen(false);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("User updated and profile image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["user"],
        });
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        queryClient.invalidateQueries({
          queryKey: ["showUserDetails"],
        });
        setDrawerOpen(false);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to update this user.");
    },
  });

  const schoolStaffBackTo = "/workspace/school?tab=staff";
  const backTo =
    location.state?.backTo || (isSchoolRolesWorkspaceView ? schoolStaffBackTo : "/users");
  const userImage =
    user?.image || user?.image_url || user?.profile_image_url || user?.avatar_url || user?.avatar;
  const displayName = user?.full_name || user?.name || "Unknown user";

  const staffAssignmentsForUser = useMemo(
    () =>
      (schoolAssignments || []).filter(
        (assignment: any) => String(assignment?.user_id) === String(id),
      ),
    [id, schoolAssignments],
  );

  const activeStaffAssignments = useMemo(
    () => staffAssignmentsForUser.filter((assignment: any) => assignment?.active !== false),
    [staffAssignmentsForUser],
  );

  const primaryStaffRole =
    activeStaffAssignments[0]?.role || staffAssignmentsForUser[0]?.role || user?.role || null;

  const staffLocation = useMemo(
    () => [user?.city, user?.state, user?.country].filter(Boolean).join(", ") || "Not set",
    [user?.city, user?.country, user?.state],
  );

  const staffProfileMeta = useMemo(
    () =>
      [
        ["Email", user?.email || "Unknown"],
        ["Phone", user?.phone_number || "Not set"],
        ["Location", staffLocation],
        ["Account role", formatPortalLabel(user?.role || "Unknown")],
        [
          "School roles",
          activeStaffAssignments.length
            ? activeStaffAssignments
                .map((assignment: any) => {
                  const roleLabel = formatPortalLabel(assignment.role);
                  return assignment.sport_name
                    ? `${roleLabel} · ${assignment.sport_name}`
                    : roleLabel;
                })
                .join(", ")
            : "None assigned",
        ],
        ["Member since", formatDateTime(user?.created_at)],
      ] as Array<[string, string]>,
    [
      activeStaffAssignments,
      staffLocation,
      user?.created_at,
      user?.email,
      user?.phone_number,
      user?.role,
    ],
  );

  const editDrawer = (
    <RightDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} title="Edit user">
      <AdminUserForm
        initialValues={{
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          email: user?.email || "",
          phone_number: user?.phone_number || "",
          street_address: user?.street_address || "",
          city: user?.city || "",
          state: user?.state || "",
          country: user?.country || "",
          zip_code: user?.zip_code || "",
          role: user?.role || "user",
          image: userImage || "",
          password: "",
          password_confirmation: "",
        }}
        isSubmitting={updateMutation.isPending}
        onCancel={() => setDrawerOpen(false)}
        onSubmit={(values) => updateMutation.mutate(values)}
        requirePassword={false}
        submitLabel="Save changes"
      />
    </RightDrawer>
  );

  const headerActions = (
    <>
      <ViewUserSecondaryButton onClick={() => history.push(backTo)} type="button">
        <FaArrowLeft size={12} />
        Back
      </ViewUserSecondaryButton>
      {canEditUser ? (
        <ViewUserPrimaryButton onClick={() => setDrawerOpen(true)} type="button">
          <FaEdit size={12} />
          Edit user
        </ViewUserPrimaryButton>
      ) : null}
    </>
  );

  if (isSchoolRolesWorkspaceView) {
    return (
      <AppLayout route="/workspace/school">
        <PageHeader breadcrumbLabel={displayName} title={displayName} actions={headerActions} />

        <SectionCard title="Staff profile">
          <SchoolPlayerProfileHeroPanel>
            <SchoolPlayerProfileHeroImage
              alt={displayName}
              fallbackLabel={displayName}
              src={userImage}
              variant="user"
            />
            <SchoolPlayerProfileHeroBody>
              <SchoolPlayerProfileHeroTextGroup>
                <SchoolPlayerProfileHeroEyebrow>
                  {formatPortalLabel(primaryStaffRole || "Staff")}
                </SchoolPlayerProfileHeroEyebrow>
                <SchoolPlayerProfileHeroTitle>{displayName}</SchoolPlayerProfileHeroTitle>
                <SchoolPlayerProfileHeroMeta>
                  {[user?.email, staffLocation !== "Not set" ? staffLocation : null]
                    .filter(Boolean)
                    .join(" · ") || "No contact details"}
                </SchoolPlayerProfileHeroMeta>
                <SchoolPlayerProfileBadgeRow>
                  {activeStaffAssignments.length > 0 ? (
                    activeStaffAssignments.map((assignment: any) => (
                      <StatusPill
                        key={String(assignment.id || `${assignment.role}-${assignment.sport_id}`)}
                        value={
                          assignment.sport_name
                            ? `${formatPortalLabel(assignment.role)} · ${assignment.sport_name}`
                            : formatPortalLabel(assignment.role)
                        }
                      />
                    ))
                  ) : primaryStaffRole ? (
                    <StatusPill value={formatPortalLabel(primaryStaffRole)} />
                  ) : null}
                </SchoolPlayerProfileBadgeRow>
              </SchoolPlayerProfileHeroTextGroup>

              <SchoolPlayerProfileMetadataGrid>
                {staffProfileMeta.map(([label, value]) => (
                  <SchoolPlayerProfileMetadataTile key={label}>
                    <SchoolPlayerProfileEyebrowText>{label}</SchoolPlayerProfileEyebrowText>
                    <SchoolPlayerProfileTitleText>{value}</SchoolPlayerProfileTitleText>
                  </SchoolPlayerProfileMetadataTile>
                ))}
              </SchoolPlayerProfileMetadataGrid>
            </SchoolPlayerProfileHeroBody>
          </SchoolPlayerProfileHeroPanel>
        </SectionCard>

        {editDrawer}
      </AppLayout>
    );
  }

  return (
    <AppLayout route="/users">
      <PageHeader
        title={user?.full_name || "User profile"}
        description="Inspect account details, wallet data, order activity, payments, and any player linkages from the same view."
        crumbs={[
          {
            label: "Dashboard",
            to: "/dashboard",
          },
          {
            label: "Users",
            to: "/users",
          },
          {
            label: user?.full_name || "User",
          },
        ]}
        actions={headerActions}
      />

      <ViewUserGridLayout>
        <StatCard label="Role" value={<StatusPill value={user?.role} />} />
        <StatCard
          label="Wallet"
          value={user?.wallet ? formatMoney(user.wallet.balance) : "Not available"}
        />
        <StatCard label="Orders" value={user?.orders?.length ?? 0} />
        <StatCard label="Payments" value={user?.payments?.length ?? 0} />
      </ViewUserGridLayout>

      <ViewUserGridLayoutSecondary>
        <SectionCard subtitle="Account identity and profile details" title="Overview">
          <ViewUserGridLayoutTertiary>
            {userImage ? (
              <ViewUserSection>
                <ViewUserEyebrowText>Profile image</ViewUserEyebrowText>
                <ViewUserPreviewImage
                  alt={user?.full_name || user?.name || "User"}
                  fallbackLabel={user?.full_name || user?.email}
                  src={userImage}
                  variant="user"
                />
                <ViewUserTitleText>{displayName}</ViewUserTitleText>
                <ViewUserMetaText>
                  {user?.role || "Unknown role"}
                  {user?.email ? ` | ${user.email}` : ""}
                </ViewUserMetaText>
              </ViewUserSection>
            ) : null}
            <ViewUserDetailsGrid>
              <ViewUserSectionSecondary>
                <ViewUserEyebrowText>Email</ViewUserEyebrowText>
                <ViewUserTitleText>{user?.email || "Unknown"}</ViewUserTitleText>
              </ViewUserSectionSecondary>
              <ViewUserSectionSecondary>
                <ViewUserEyebrowText>Phone</ViewUserEyebrowText>
                <ViewUserTitleText>{user?.phone_number || "Not set"}</ViewUserTitleText>
              </ViewUserSectionSecondary>
              <ViewUserSectionSecondary>
                <ViewUserEyebrowText>Location</ViewUserEyebrowText>
                <ViewUserTitleText>{staffLocation}</ViewUserTitleText>
              </ViewUserSectionSecondary>
              <ViewUserSectionSecondary>
                <ViewUserEyebrowText>Street address</ViewUserEyebrowText>
                <ViewUserTitleText>{user?.street_address || "Not set"}</ViewUserTitleText>
              </ViewUserSectionSecondary>
              <ViewUserSectionSecondary>
                <ViewUserEyebrowText>Zip code</ViewUserEyebrowText>
                <ViewUserTitleText>{user?.zip_code || "Not set"}</ViewUserTitleText>
              </ViewUserSectionSecondary>
              <ViewUserSectionSecondary>
                <ViewUserEyebrowText>Created</ViewUserEyebrowText>
                <ViewUserTitleText>{formatDateTime(user?.created_at)}</ViewUserTitleText>
              </ViewUserSectionSecondary>
            </ViewUserDetailsGrid>
          </ViewUserGridLayoutTertiary>
        </SectionCard>

        <SectionCard subtitle="Wallet and player linkage" title="Linked Resources">
          <ViewUserGridLayoutQuaternary>
            <ViewUserSectionSecondary>
              <ViewUserEyebrowText>Wallet</ViewUserEyebrowText>
              <ViewUserTitleText>
                {user?.wallet
                  ? `${formatMoney(user.wallet.balance)} in ${user.wallet.currency}`
                  : "No wallet"}
              </ViewUserTitleText>
              {user?.wallet ? (
                <ViewUserMetaText>
                  {user.wallet.transaction_count} transactions tracked for this account.
                </ViewUserMetaText>
              ) : null}
            </ViewUserSectionSecondary>
            <ViewUserSectionSecondary>
              <ViewUserEyebrowText>Linked player</ViewUserEyebrowText>
              <ViewUserTitleText>
                {user?.player?.full_name || "No linked athlete"}
              </ViewUserTitleText>
              {user?.player ? (
                <ViewUserMetaText>
                  {user.player.position} with team ID {user.player.team_id || "N/A"}.
                </ViewUserMetaText>
              ) : null}
            </ViewUserSectionSecondary>
          </ViewUserGridLayoutQuaternary>
        </SectionCard>
      </ViewUserGridLayoutSecondary>

      <ViewUserGridLayoutQuinary>
        <SectionCard subtitle="Recent order activity" title="Orders">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Item",
                accessor: (row: any) => row.merchandise_item?.name || "Unknown",
              },
              {
                label: "Status",
                accessor: (row: any) => <StatusPill value={row.status} />,
              },
              {
                label: "Total",
                accessor: (row: any) => row.formatted_total_price || formatMoney(row.total_price),
              },
              {
                label: "Placed",
                accessor: (row: any) => formatDateTime(row.created_at),
              },
            ]}
            data={user?.orders || []}
            hideNav
          />
        </SectionCard>

        <SectionCard subtitle="Recent payment activity" title="Payments">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Target",
                accessor: (row: any) => row.payment_target_name || "Unknown",
              },
              {
                label: "Type",
                accessor: (row: any) => row.payment_target_type || "Unknown",
              },
              {
                label: "Amount",
                accessor: (row: any) => row.formatted_amount || formatMoney(row.amount),
              },
              {
                label: "Status",
                accessor: (row: any) => <StatusPill value={row.status} />,
              },
            ]}
            data={user?.payments || []}
            hideNav
          />
        </SectionCard>
      </ViewUserGridLayoutQuinary>

      {editDrawer}
    </AppLayout>
  );
};
export default ViewUserPage;
